import {
  Action,
  ActionCreator,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { batch } from 'react-redux';

import { ThunkResult } from '@/shared/actions/thunk-result';

import { createSelectors } from './selectors';
import {
  IFsm,
  TMachineConfig,
  TActionConfig,
  TFsmContext,
  TGetFsmState,
  TOnUpdate,
  TSelectors,
  TDraft,
} from './types';

interface ISendParams<TEvent extends string> {
  event: TEvent;
  newContext?: TFsmContext;
}

interface ISetFsmValueParams<TValue extends string> {
  newValue: TValue;
  newContext?: TFsmContext;
}

const createAsyncActions = <
  TValue extends string,
  TEvent extends string,
  TAction extends string,
  IDraft extends TDraft,
>(
  namespace: string,
  selectors: TSelectors<TValue, TEvent, TAction, IDraft>,
  effects: TActionConfig<TAction>,
  actions: {
    setValue: ActionCreator<Action>;
    setIsBusy: ActionCreator<Action>;
    setOnUpdate: ActionCreator<Action>;
    setContext: ActionCreator<Action>;
  },
) => {
  const trySetValue = createAsyncThunk(
    `${namespace}/try-set-fsm-value`,
    async (
      args: ISetFsmValueParams<TValue>,
      { dispatch, getState },
    ): Promise<ThunkResult<string>> => {
      try {
        if (selectors.isBusy(getState())) {
          return { isSucceed: false, result: 'transition already in progress' };
        }

        batch(() => {
          dispatch(actions.setIsBusy(true));

          dispatch(actions.setContext(args.newContext || {}));
        });

        const context = selectors.getContext(getState());
        const config = selectors.getConfig(getState());
        const entryEffectKey = config[args.newValue].onEntry;

        if (entryEffectKey !== undefined) {
          const entryEffect = effects[entryEffectKey];

          await dispatch(entryEffect(context))?.unwrap?.();
        }

        dispatch(actions.setValue(args.newValue));

        const onUpdate = selectors.getOnUpdateHandler(getState());

        if (onUpdate) {
          onUpdate(args.newValue, context);
        }

        return { isSucceed: true };
      } finally {
        dispatch(actions.setIsBusy(false));
      }
    },
  );

  const sendEvent = createAsyncThunk(
    `${namespace}/send-event`,
    async (
      { event, newContext }: ISendParams<TEvent>,
      { dispatch, getState },
    ): Promise<ThunkResult<string>> => {
      const transitions = selectors.getTransitions(getState());
      const transition = transitions[event];

      if (transition !== undefined) {
        const newValue =
          typeof transition.target === 'string'
            ? transition.target
            : transition.target(newContext || {});
        const transitionResult = await dispatch(trySetValue({ newValue, newContext })).unwrap();

        if (transitionResult.isSucceed) {
          const context = selectors.getContext(getState());

          transition.effects?.forEach(effectKey => {
            const effect = effects[effectKey];

            if (effect) {
              dispatch(effect(context))?.unwrap?.();
            }
          });

          return { isSucceed: true };
        } else {
          return transitionResult;
        }
      }

      return { isSucceed: false };
    },
  );

  return {
    trySetValue,
    sendEvent,
  };
};

interface IParams<TValue extends string, TEvent extends string, TAction extends string> {
  config: TMachineConfig<TValue, TEvent, TAction>;
  currentValue: TValue;
  effects: TActionConfig<TAction>;
  namespace: string;
}

const createFsmSlice = <
  TValue extends string,
  TEvent extends string,
  TAction extends string,
  IDraft extends TDraft,
>({
  config,
  currentValue,
  effects,
  namespace,
}: IParams<TValue, TEvent, TAction>) => {
  const getSliceState: TGetFsmState<TValue, TEvent, TAction, IDraft> = (state: any) =>
    state[namespace];
  const fsmSelectors = createSelectors(getSliceState);
  const fsmSlice = createSlice({
    name: namespace,
    initialState: <IFsm<TValue, TEvent, TAction, IDraft>>{
      config,
      currentValue,
      isBusy: false,
      context: {},
      onUpdate: undefined,
      isInitialized: false,
      draft: {},
    },
    reducers: {
      setValue: (state, action: PayloadAction<TValue>) => {
        state.currentValue = action.payload as any; // Type 'TValue' is not assignable to type 'Draft<TValue>'

        if (!state.isInitialized) {
          state.isInitialized = true;
        }
      },
      setIsBusy: (state, action: PayloadAction<boolean>) => {
        state.isBusy = action.payload;
      },
      setContext: (state, action: PayloadAction<TFsmContext>) => {
        state.context = action.payload;
      },
      setDraft: (state, action: PayloadAction<Partial<IDraft>>) => {
        state.draft = {
          ...state.draft,
          ...action.payload,
        };
      },
      setOnUpdate: (state, action: PayloadAction<TOnUpdate<TValue>>) => {
        state.onUpdate = action.payload;
      },
      patchTransitions: (
        state,
        action: PayloadAction<Partial<TMachineConfig<TValue, TEvent, TAction>>>,
      ) => {
        Object.assign(state.config, action.payload);
      },
      removeTransition: (state, action: PayloadAction<{ fsmValue: TValue; fsmEvent: TEvent }>) => {
        const { fsmValue, fsmEvent } = action.payload;
        const configDraft = <TMachineConfig<TValue, TEvent, TAction>>state.config;

        if (configDraft[fsmValue].on[fsmEvent]) {
          delete configDraft[fsmValue].on[fsmEvent];
        }
      },
    },
  });
  const fsmAsyncActions = createAsyncActions(namespace, fsmSelectors, effects, fsmSlice.actions);

  return { fsmSlice, fsmSelectors, fsmAsyncActions };
};

export { createFsmSlice };
